import { z } from 'zod';

export const informationCreateSchema = z
  .object({
    title: z
      .string()
      .min(1, 'タイトルは必須です。')
      .max(100, 'タイトルは100文字以下にしてください。'),
    information_date: z.string().min(1, 'お知らせ日は必須です。'),
    contents: z
      .string()
      .max(3000, 'お知らせ内容は3000文字以下にしてください。')
      .nullable()
      .optional(),
    display_from_date: z.string().nullable().optional(),
    display_from_time: z.string().nullable().optional(),
    display_to_date: z.string().nullable().optional(),
    display_to_time: z.string().nullable().optional(),
    is_display: z.boolean(),
    service_type_a2: z.boolean(),
    service_type_r2: z.boolean(),
    tenant_ids: z.array(z.number()).min(1, '表示対象は必須です。'),
    clinic_ids: z.array(z.number()).optional().default([]),
  })
  .refine(
    data => data.service_type_a2 || data.service_type_r2,
    {
      message: '表示サービスは必須です。',
      path: ['service_type_a2'],
    }
  )
  .superRefine((data, ctx) => {
    const bothDatesNoTimes =
      !!data.display_from_date &&
      !!data.display_to_date &&
      !data.display_from_time &&
      !data.display_to_time;

    if (bothDatesNoTimes) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: '適用日が存在する時、適用時間は必須です。',
        path: ['display_from_time'],
      });
    } else {
      if (data.display_from_date && !data.display_from_time) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: '適用開始日が存在する時、適用開始時間は必須です。',
          path: ['display_from_time'],
        });
      }
      if (data.display_to_date && !data.display_to_time) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: '適用終了日が存在する時、適用終了時間は必須です。',
          path: ['display_to_time'],
        });
      }
    }
  })
  .refine(
    data => {
      if ((data.display_to_date || data.display_to_time) && !data.display_from_date) return false;
      return true;
    },
    {
      message: '適用開始日を入力してください。',
      path: ['display_from_date'],
    }
  )
  .refine(
    data => {
      if (data.display_from_date && data.display_to_date) {
        const from = `${data.display_from_date} ${data.display_from_time || '00:00'}`;
        const to = `${data.display_to_date} ${data.display_to_time || '00:00'}`;
        return new Date(from) <= new Date(to);
      }
      return true;
    },
    {
      message: '適用終了日時は適用開始日時より後の日時を指定してください。',
      path: ['display_to_date'],
    }
  );

export type InformationCreateFormData = z.infer<typeof informationCreateSchema>;

export const informationEditSchema = z
  .object({
    title: z
      .string()
      .min(1, 'タイトルは必須です。')
      .max(100, 'タイトルは100文字以下にしてください。'),
    information_date: z.string().min(1, 'お知らせ日は必須です。'),
    contents: z
      .string()
      .max(3000, 'お知らせ内容は3000文字以下にしてください。')
      .nullable()
      .optional(),
    display_from_date: z.string().nullable().optional(),
    display_from_time: z.string().nullable().optional(),
    display_to_date: z.string().nullable().optional(),
    display_to_time: z.string().nullable().optional(),
    is_display: z.boolean(),
    service_type_a2: z.boolean(),
    service_type_r2: z.boolean(),
    tenant_ids: z.array(z.number()).min(1, '表示対象は必須です。'),
    clinic_ids: z.array(z.number()).optional().default([]),
  })
  .refine(
    data => data.service_type_a2 || data.service_type_r2,
    {
      message: '表示サービスは必須です。',
      path: ['service_type_a2'],
    }
  )
  .superRefine((data, ctx) => {
    const bothDatesNoTimes =
      !!data.display_from_date &&
      !!data.display_to_date &&
      !data.display_from_time &&
      !data.display_to_time;

    if (bothDatesNoTimes) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: '適用日が存在する時、適用時間は必須です。',
        path: ['display_from_time'],
      });
    } else {
      if (data.display_from_date && !data.display_from_time) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: '適用開始日が存在する時、適用開始時間は必須です。',
          path: ['display_from_time'],
        });
      }
      if (data.display_to_date && !data.display_to_time) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: '適用終了日が存在する時、適用終了時間は必須です。',
          path: ['display_to_time'],
        });
      }
    }
  })
  .refine(
    data => {
      if ((data.display_to_date || data.display_to_time) && !data.display_from_date) return false;
      return true;
    },
    {
      message: '適用開始日を入力してください。',
      path: ['display_from_date'],
    }
  )
  .refine(
    data => {
      if (data.display_from_date && data.display_to_date) {
        const from = `${data.display_from_date} ${data.display_from_time || '00:00'}`;
        const to = `${data.display_to_date} ${data.display_to_time || '00:00'}`;
        return new Date(from) <= new Date(to);
      }
      return true;
    },
    {
      message: '適用終了日時は適用開始日時より後の日時を指定してください。',
      path: ['display_to_date'],
    }
  );

export type InformationEditFormData = z.infer<typeof informationEditSchema>;

/**
 * Convert checkbox booleans to service_type value for API.
 * A2 + R2 checked => 'BOTH', only A2 => 'A2', only R2 => 'R2', none => null
 */
export function resolveServiceType(a2: boolean, r2: boolean): string | null {
  if (a2 && r2) return 'BOTH';
  if (a2) return 'A2';
  if (r2) return 'R2';
  return null;
}

/**
 * Merge date + time fields into datetime string for API.
 * Returns null if date is empty.
 */
export function mergeDateTime(date: string | null | undefined, time: string | null | undefined): string | null {
  if (!date) return null;
  return time ? `${date} ${time}` : `${date} 00:00`;
}
